/**
 * Cross-chain swap poll engine.
 *
 * This is the durable half of the swap tracker: a self-scheduling poll loop that
 * lives outside any React component so it survives a navigation away from the
 * Swap screen and a page reload. The earlier implementation polled a fixed
 * number of ticks tied to the Swap view's AbortController, so leaving the screen
 * aborted the loop and a testnet fill that lands about a minute later was missed.
 * Here the loop is owned by the module, driven by the persisted pendingSwaps
 * store slice, and stops only when the swap reaches a canonical terminal state
 * or the hard tracking cap elapses.
 *
 * Each tracked swap gets one loop, guarded so a duplicate start (from both a
 * fresh execute and a boot resume, say) never double-polls the same id. A tick
 * reads the swap service status, folds the freshest status and destination hash
 * into the store (which persists it), and reschedules itself at the provider's
 * cadence until the swap settles. Once elapsed exceeds the quote's ETA the swap
 * is flagged delayed for the taking-longer overlay while polling continues, and
 * a whole-swap cap stops a loop that would otherwise poll a stuck swap forever.
 *
 * The engine holds no React state. The UI reads the same store slice the engine
 * writes, so the stepper and banner repaint from the persisted record without
 * the engine knowing they exist.
 */

import type { PendingSwap } from './types';
import { getSlice, upsertPendingSwap, removePendingSwap, pushToast } from '@/store/store';
import { getSwapStatus, reportSwapOriginTx, type SwapStatus } from '@/lib/swapApi';
import { pollOriginTransaction, isValidTxHash } from '@/lib/swapExecute';
import { isBackendTerminal, toUiState, CLIENT_TRACK_CAP_STATUS } from '@/lib/swapStatus';
import { blocksNewAttempt } from '@/lib/originStatus';

/**
 * Per-provider poll cadence in milliseconds. Relay's status indexer moves fast,
 * so it is polled every few seconds; Across's indexer lags the fill by up to
 * fifteen seconds, so a tighter interval only wastes requests. A provider not in
 * this map falls back to the default cadence.
 */
const PROVIDER_INTERVAL_MS: Record<string, number> = {
  relay: 3000,
  across: 10000,
};

/** Poll cadence for a provider without a specific entry. */
const DEFAULT_INTERVAL_MS = 6000;

/**
 * The point past which a still-unfilled swap is shown as taking longer than
 * expected. The quote's ETA is preferred; this is the fallback when the quote
 * carried none, so a swap with no ETA still eventually surfaces the delayed
 * overlay rather than looking stuck indefinitely without explanation.
 */
const DEFAULT_ETA_MS = 90_000;

/**
 * The hard ceiling on how long a single swap is tracked. Cross-chain fills
 * normally settle in a minute or two; a swap still non-terminal after this
 * window has the client stop polling so the loop ends and the record leaves the
 * persisted set rather than polling for the life of the tab. Reaching this cap
 * is not a failure: the server tracker keeps its own record and the swap may
 * still settle, so it is marked with the neutral client-cap sentinel and shown
 * as taking-longer, not as an error. Activity can still show a late settlement;
 * this only bounds the foreground poll.
 */
const MAX_TRACK_MS = 30 * 60 * 1000;

/**
 * Keep a terminal result visible long enough for the inline stepper and toast
 * to communicate the outcome, then leave progress tracking. The durable swap
 * remains available in Activity through the server-backed feed.
 */
const TERMINAL_LINGER_MS = 12_000;

/**
 * A small seam over setTimeout/clearTimeout and Date.now so the engine can be
 * driven by a fake clock in tests. The default binds the real timers and clock.
 * A test installs a controllable scheduler through {@link __setSchedulerForTest}.
 */
export interface Scheduler {
  setTimeout(fn: () => void, ms: number): number;
  clearTimeout(handle: number): void;
  now(): number;
}

const realScheduler: Scheduler = {
  setTimeout: (fn, ms) => setTimeout(fn, ms) as unknown as number,
  clearTimeout: (handle) => clearTimeout(handle),
  now: () => Date.now(),
};

let scheduler: Scheduler = realScheduler;

/**
 * The status-fetch function the engine polls with, injectable for tests so the
 * loop can be exercised without the network. The default is the real swap-status
 * call; a test swaps in a fake that returns scripted statuses.
 */
export type StatusPoller = (provider: string, pollKey: string) => Promise<SwapStatus | null>;

const realPoller: StatusPoller = (provider, pollKey) => getSwapStatus(provider, pollKey);

let statusPoller: StatusPoller = realPoller;

/**
 * The origin-hash reporter, injectable for tests. Default is the real
 * swap-service report; the tick retries it until it lands (originReported only
 * flips on a fulfilled report, and the server treats a same-hash re-report as a
 * no-op).
 */
export type OriginReporter = (trackingId: string, txHash: string) => Promise<unknown>;

const realReporter: OriginReporter = (trackingId, txHash) => reportSwapOriginTx(trackingId, txHash);

let originReporter: OriginReporter = realReporter;

/**
 * Fold a FULFILLED origin report into a record. Besides the originReported
 * flag, the local origin axis advances 'unreported' → 'reported' (codex plugin
 * review, medium): the server only answers a report with 200 after atomically
 * advancing the row to at least 'reported', so mirroring that is faithful —
 * and it is what releases the form's ambiguity hold in THIS session, because
 * the hold reads originStatus, not the flag. Strictly monotone: any other
 * local state (verified, reverted, unknown) is newer information and is kept.
 */
function withReportApplied(swap: PendingSwap): PendingSwap {
  const bump = !swap.originStatus || swap.originStatus === 'unreported';
  return { ...swap, originReported: true, ...(bump ? { originStatus: 'reported' } : {}) };
}

/**
 * The origin-transaction poll used to advance the "Origin confirmed" step and
 * capture the origin hash, injectable for tests. The default is the real origin
 * poller; a swap whose origin hash is already known skips this entirely.
 */
export type OriginPoller = (
  transactionId: string,
) => Promise<{ status?: string; tx_hash?: string; [k: string]: unknown }>;

const realOriginPoller: OriginPoller = (id) => pollOriginTransaction(id);

let originPoller: OriginPoller = realOriginPoller;

/** The live loop handles, keyed by swap id, so a start is idempotent per id. */
const timers = new Map<string, number>();

/** Swaps whose origin leg is currently being polled, to avoid overlapping it. */
const originInFlight = new Set<string>();

/**
 * Swaps the user dismissed from the tracker banner, tombstoned for this session.
 *
 * Clearing the timer and dropping the record is NOT enough on its own. A tick is
 * async: it reads the record, awaits the origin and status polls, then writes the
 * result and arms the next tick. A dismissal landing inside that window has the
 * timer cleared out from under a tick that is already past its checks, and the
 * tick's own tail re-upserts the record and re-arms the loop — so the banner
 * reappears on the next render or the next reload, which is exactly what it did.
 *
 * A tombstone closes that window from the other side: an id in this set can
 * never be armed, started, or written back, whatever a tick in flight believes.
 * Session-scoped on purpose — it guards against a race measured in milliseconds,
 * not against the record legitimately coming back in a later session, and the
 * persisted record is gone by then anyway.
 */
const dismissed = new Set<string>();

/**
 * Whether a swap is already being tracked, so a second start for the same id is
 * a no-op rather than a duplicate poller.
 *
 * @param id The swap id.
 * @returns True when a loop is armed for this id.
 */
export function isTracking(id: string): boolean {
  return timers.has(id);
}

/**
 * Whether the durable tracker is already polling a swap identified by its
 * provider-scoped poll key. The Activity feed keys its rows on the swap
 * service's own request id rather than the gateway transaction id this engine
 * keys on, so a by-id check would never match across the two id spaces. Matching
 * on the provider and poll key (Relay's requestId, or Across's origin deposit
 * hash) is what lets the feed recognize a row this engine already owns and skip
 * re-polling the identical status endpoint.
 *
 * @param provider The swap provider, so two providers never share a poll key.
 * @param pollKey The provider-scoped poll key (the feed's request id).
 * @returns True when a live loop is already polling this provider and key.
 */
export function isTrackingPollKey(provider: string, pollKey: string): boolean {
  const byId = getSlice('pendingSwaps').byId;
  for (const id of timers.keys()) {
    const swap = byId[id];
    if (swap && swap.provider === provider && swap.pollKey === pollKey) return true;
  }
  return false;
}

/**
 * Start tracking one swap, if not already tracked. Seeds the persisted record
 * (so the record exists even before the first tick) and arms the first poll. A
 * swap that is already terminal or already tracked returns without arming a loop.
 *
 * @param swap The pending-swap record to track.
 */
export function start(swap: PendingSwap): void {
  // A dismissed swap never restarts, so neither a resumeAll pass nor a stray
  // start can put a banner the user closed back on their screen.
  if (dismissed.has(swap.id)) return;
  // A terminal record restored from persistence needs no loop and no second
  // receipt: Activity already contains the durable server-backed result.
  if (isBackendTerminal(swap.status)) {
    removePendingSwap(swap.id);
    return;
  }
  // Seed or refresh the store record so the UI has something to render before
  // the first tick returns. upsert is keyed by id, so this is safe to call for a
  // record that is already present.
  upsertPendingSwap(swap);
  if (timers.has(swap.id)) return;
  // Arm the first tick immediately (zero delay) so a just-submitted swap shows
  // progress without waiting a full interval.
  arm(swap.id, 0);
}

/**
 * Re-arm polling for every persisted non-terminal swap. Called once on boot (and
 * on any resume of the tracker gate) so a reload or a navigation resumes every
 * in-flight swap without the user reopening the Swap screen. Already-tracked and
 * already-terminal swaps are skipped by {@link start}.
 */
export function resumeAll(): void {
  const map = getSlice('pendingSwaps').byId;
  for (const swap of Object.values(map)) {
    start(swap);
  }
}

/**
 * Stop one running loop, without touching its persisted record.
 *
 * Used by the tracker banner's dismiss control, which calls this and then drops
 * the record. Calling it is REQUIRED, not just tidy: clearing the timer and
 * removing the record alone loses a race with a tick already in flight, whose
 * tail re-upserts the record and re-arms the loop, and the dismissed banner
 * returns. This tombstones the id (see {@link dismissed}) so no tick, arm, or
 * resume can bring it back.
 *
 * @param id The tracked swap's id.
 */
export function stop(id: string): void {
  // Tombstone FIRST, before the timer is touched: a tick that fires between the
  // two must find the id already dismissed rather than merely un-armed.
  dismissed.add(id);
  const handle = timers.get(id);
  if (handle !== undefined) scheduler.clearTimeout(handle);
  timers.delete(id);
  originInFlight.delete(id);
}

/**
 * Stop every running loop and clear the in-flight sets, without touching the
 * persisted records. Called when the tracker gate unmounts (logout), so a fresh
 * session starts clean; the persisted swaps remain so a re-login resumes them.
 */
export function stopAll(): void {
  for (const handle of timers.values()) scheduler.clearTimeout(handle);
  timers.clear();
  originInFlight.clear();
}

/** Poll interval for a provider, falling back to the default cadence. */
function intervalFor(provider: string): number {
  return PROVIDER_INTERVAL_MS[provider] ?? DEFAULT_INTERVAL_MS;
}

/** Arm the next tick for a swap after a delay, replacing any pending timer. */
function arm(id: string, delayMs: number): void {
  // The one place every scheduled tick comes from, which makes it the one place
  // that has to honor a dismissal. A tick whose tail arms the next one runs this
  // after the user may have clicked dismiss, so the check belongs here and not
  // only at the call sites.
  if (dismissed.has(id)) return;
  const existing = timers.get(id);
  if (existing !== undefined) scheduler.clearTimeout(existing);
  const handle = scheduler.setTimeout(() => {
    void tick(id);
  }, delayMs);
  timers.set(id, handle);
}

/**
 * Settle a swap that reached a terminal state: stop its poll loop, briefly keep
 * the final state visible, then remove it from the pending tracker.
 */
function settle(id: string): void {
  const existing = timers.get(id);
  if (existing !== undefined) scheduler.clearTimeout(existing);
  originInFlight.delete(id);
  // LAST-CHANCE ORIGIN REPORT (B3). The settling tick is the FINAL tick:
  // if the per-tick report above it failed transiently — or the hash and the
  // terminal status arrived on the same tick — there is no later tick to retry,
  // leaving the server row 'unreported' while the client knew the hash. A
  // duplicate of an already-landed report is a server-side no-op, so firing it
  // unconditionally is safe.
  const swap = getSlice('pendingSwaps').byId[id];
  if (swap?.trackingId && !swap.originReported && isValidTxHash(swap.originTxHash)) {
    const trackingId = swap.trackingId;
    const hash = swap.originTxHash as string;
    void originReporter(trackingId, hash)
      .then(() => {
        const latest = getSlice('pendingSwaps').byId[id];
        if (latest) upsertPendingSwap(withReportApplied(latest));
      })
      .catch(() => {
        // The row stays 'unreported' server-side, which is honest — it never
        // claims nothing broadcast.
      });
  }
  const handle = scheduler.setTimeout(() => {
    timers.delete(id);
    removePendingSwap(id);
  }, TERMINAL_LINGER_MS);
  timers.set(id, handle);
}

/**
 * One poll tick for a swap: read the current record, poll the origin leg when
 * its hash is not yet known, poll the cross-chain status, fold the result into
 * the store, and either finish on a terminal state or reschedule.
 */
async function tick(id: string): Promise<void> {
  const swap = getSlice('pendingSwaps').byId[id];
  // The record was removed (settled or dropped) between arming and firing; let
  // the loop die.
  if (!swap) {
    const handle = timers.get(id);
    if (handle !== undefined) scheduler.clearTimeout(handle);
    timers.delete(id);
    return;
  }

  const now = scheduler.now();

  // Hard cap: a swap still non-terminal after the tracking window has the client
  // stop polling. This is not a failure, only the end of the foreground poll, so
  // the record takes the neutral client-cap sentinel (which maps to the settling
  // UI state) rather than a failure status. The store record is updated first so
  // any open stepper reflects the taking-longer state, the neutral info toast
  // fires, then the record is removed after the linger.
  if (now - swap.startedAt > MAX_TRACK_MS) {
    // THE LATCH OUTLIVES THE POLL (B1). For an AMBIGUOUS record whose origin
    // axis still says a broadcast may be outstanding, the cap may end the
    // foreground poll but must NOT remove the record: the record IS the
    // double-spend gate, and aging it out would silently reopen the form 30
    // minutes after a lost response — precisely when the user has stopped
    // watching. The record stays until the server's origin axis answers
    // (reconcileOrigins on every resume) or the user explicitly acknowledges
    // it away in the banner. This also covers a latch that was never pollable
    // at all (a provisional with no usable poll key): its durability cannot
    // depend on pollability.
    if (swap.ambiguous && blocksNewAttempt(swap.originStatus)) {
      const handle = timers.get(id);
      if (handle !== undefined) scheduler.clearTimeout(handle);
      timers.delete(id);
      originInFlight.delete(id);
      return;
    }
    upsertPendingSwap({ ...swap, status: CLIENT_TRACK_CAP_STATUS, updatedAt: now });
    notifyTerminal({ ...swap, status: CLIENT_TRACK_CAP_STATUS });
    settle(id);
    return;
  }

  // Advance the origin leg when the deposit hash is not yet known. The origin
  // transaction id is the swap's own id; polling it captures the broadcast hash,
  // which flips the stepper's "Origin confirmed" step. Guarded so a slow origin
  // poll never overlaps the next status tick for the same swap.
  let originTxHash = swap.originTxHash;
  if (!originTxHash && !originInFlight.has(id)) {
    originInFlight.add(id);
    try {
      const record = await originPoller(id);
      const hash = typeof record.tx_hash === 'string' ? record.tx_hash : null;
      if (hash) originTxHash = hash;
    } catch {
      // A transient origin-poll failure is not fatal: the cross-chain status is
      // still worth reading, and the next tick retries the origin hash.
    } finally {
      originInFlight.delete(id);
    }
  }

  // LATE-HASH REPORTING (the Across-shaped gap). For most Across swaps the
  // deposit hash is not known at execute time, so the execute-path report never
  // fires and the server row would sit 'unreported' forever while this tracker
  // — the one component that DOES learn the hash — told it nothing. Report it
  // here, and retry on later ticks until it lands: originReported only flips on
  // a fulfilled report, and the server treats a same-hash re-report as a no-op,
  // so the retry is idempotent.
  if (swap.trackingId && !swap.originReported && isValidTxHash(originTxHash)) {
    const trackingId = swap.trackingId;
    const hash = originTxHash as string;
    void originReporter(trackingId, hash)
      .then(() => {
        const latest = getSlice('pendingSwaps').byId[id];
        if (latest) upsertPendingSwap(withReportApplied(latest));
      })
      .catch(() => {
        // Swallowed by design: the swap already happened, and the next tick
        // retries. The row stays 'unreported' server-side, which is honest.
      });
  }

  // Resolve the poll key for this tick, honoring the Relay/Across asymmetry.
  // Relay's poll key is the requestId minted at quote time, which is itself the
  // status key, so it is used as-is and polled from the first tick. Across keys
  // its fill strictly on the origin deposit tx hash and 400s on anything else, so
  // its poll key must be that hash. The execute path could not always supply it
  // (the deposit may not have broadcast yet), so when an Across swap's stored
  // pollKey is not a valid 0x hash, back-fill it here from the origin hash the
  // origin poll just captured.
  const isAcross = ['across', 'mayan'].includes(swap.provider.toLowerCase());
  let pollKey = swap.pollKey;
  if (isAcross && !isValidTxHash(pollKey) && isValidTxHash(originTxHash)) {
    pollKey = originTxHash;
  }

  // Issue the cross-chain status poll only when the poll key is usable. For
  // Across that means a valid 0x deposit hash; polling it with the quote id (or an
  // empty key) is a guaranteed 400, so the status call is skipped until the hash
  // is known while the origin-confirmed step still advances from the origin poll.
  // Relay's requestId is always a usable key, so it polls immediately.
  const pollKeyReady = isAcross ? isValidTxHash(pollKey) : Boolean(pollKey);
  let status: SwapStatus | null = null;
  if (pollKeyReady) {
    try {
      status = await statusPoller(swap.provider, pollKey);
    } catch {
      // A transient status failure should not tear down tracking. Keep the last
      // known record and reschedule; a persistent failure still ends at the cap.
    }
  }

  // The record may have been removed while the awaits were pending (a terminal
  // tick raced this one, or the user dismissed the banner). Re-read and bail if
  // it is gone or tombstoned, so nothing below writes it back.
  const stillTracked = dismissed.has(id) ? undefined : getSlice('pendingSwaps').byId[id];
  if (!stillTracked) {
    const handle = timers.get(id);
    if (handle !== undefined) scheduler.clearTimeout(handle);
    timers.delete(id);
    return;
  }

  // Fold the freshest observation into the record. The backend status is stored
  // as-is (the UI derives its state from it); the destination hash and expected
  // out are only overwritten when the poll actually carried a value, so a later
  // empty poll never clears a hash an earlier one captured.
  const nextStatus = status?.status ?? stillTracked.status;
  const merged: PendingSwap = {
    ...stillTracked,
    status: nextStatus,
    subStatus: status?.subStatus ?? stillTracked.subStatus,
    originTxHash: originTxHash ?? stillTracked.originTxHash,
    // Persist the back-filled Across poll key so subsequent ticks (and a resume
    // after a reload) poll the deposit hash directly rather than re-deriving it.
    // Relay's key never changes, so this is a no-op there.
    pollKey,
    destTxHash: status?.dstTxHash ?? stillTracked.destTxHash,
    // Across reports the delivered amount live; prefer it over the quoted
    // expectation once present so the delivered step shows the real received
    // amount rather than the estimate.
    expectedOut: status?.amountOut
      ? status.amountOut
      : stillTracked.expectedOut,
    updatedAt: now,
  };

  if (isBackendTerminal(nextStatus)) {
    // Paint the terminal state and notify once, then let settle remove this
    // progress record after the short linger. Activity remains the receipt.
    upsertPendingSwap(merged);
    notifyTerminal(merged);
    settle(id);
    return;
  }

  upsertPendingSwap(merged);
  arm(id, intervalFor(swap.provider));
}

/**
 * Fire the terminal toast for a settled swap, matched to the toast kind that
 * fits the outcome. Delivered and partial fills read as a success. A refund
 * reads as a neutral info (the input came back, not an error). A settling swap,
 * whether the client's own tracking window elapsed or the provider reported an
 * expired/timeout lapse, also reads as neutral info: it may still settle, and an
 * unfilled swap is refunded to the wallet, so an error toast would overstate the
 * outcome. Only a genuine provider failure reads as an error. The toast kinds
 * are the store's fixed set (success, info, error), so the non-error outcomes
 * use info to avoid the red error styling.
 */
function notifyTerminal(swap: PendingSwap): void {
  const uiState = toUiState(swap.status, Boolean(swap.originTxHash));
  const route = `${swap.sendSymbol} → ${swap.outSymbol}`;
  if (uiState === 'delivered') {
    const received = swap.expectedOut ? `${swap.expectedOut} ${swap.outSymbol}` : swap.outSymbol;
    pushToast('success', `Swap delivered: received ${received}.`);
  } else if (uiState === 'refunded') {
    pushToast('info', `Swap refunded to your wallet (${route}).`);
  } else if (uiState === 'settling') {
    pushToast('info', 'This swap is taking longer than usual. You can keep following it in Activity.');
  } else {
    pushToast('error', `Swap failed (${route}).`);
  }
}

/**
 * Whether a swap should show the taking-longer-than-expected overlay: it is
 * still non-terminal and has been running longer than its ETA (or the default
 * fallback when it carried none). The provider sub-status ("delayed",
 * "waiting", "slowfill") also trips it early, since the provider itself is
 * signaling a slow fill before the ETA elapses.
 *
 * @param swap The tracked swap.
 * @param now Epoch milliseconds, injectable so a caller can pass a fixed clock.
 * @returns True when the delayed overlay should show.
 */
export function isDelayed(swap: PendingSwap, now: number = Date.now()): boolean {
  if (isBackendTerminal(swap.status)) return false;
  const sub = (swap.subStatus ?? '').toLowerCase();
  if (sub === 'delayed' || sub === 'waiting' || sub === 'slowfill' || sub === 'slowfillrequested') {
    return true;
  }
  const etaMs = swap.etaSeconds && swap.etaSeconds > 0 ? swap.etaSeconds * 1000 : DEFAULT_ETA_MS;
  return now - swap.startedAt > etaMs;
}

/**
 * Install a fake scheduler, status poller, and origin poller for tests, and
 * return a restore function. The real implementations are captured so a test can
 * put them back, keeping suites isolated.
 *
 * @param overrides The seams to replace; any omitted keeps the real one.
 * @returns A function that restores the real seams and stops all loops.
 */
export function __setSchedulerForTest(overrides: {
  scheduler?: Scheduler;
  status?: StatusPoller;
  origin?: OriginPoller;
  report?: OriginReporter;
}): () => void {
  const prevScheduler = scheduler;
  const prevStatus = statusPoller;
  const prevOrigin = originPoller;
  const prevReporter = originReporter;
  if (overrides.scheduler) scheduler = overrides.scheduler;
  if (overrides.status) statusPoller = overrides.status;
  if (overrides.origin) originPoller = overrides.origin;
  if (overrides.report) originReporter = overrides.report;
  return () => {
    stopAll();
    scheduler = prevScheduler;
    statusPoller = prevStatus;
    originPoller = prevOrigin;
    originReporter = prevReporter;
  };
}
